import { useState, useEffect } from 'react';
import { FormDialog } from '../../components/FormDialog';
import { useTranslation } from 'react-i18next';
import { resolveValidationMessage } from '../../utils/validationMessages';
import { type DemandDto } from '../../api/models/DemandDto';
import { type CrudFormProps } from '../../components/CrudPage';
import { MaterialControllerService, SystemSettingControllerService, type MaterialDto } from '../../api';
import { DropDownListComponent } from '@syncfusion/ej2-react-dropdowns';
import { useDropdownFormInitializer } from '../../hooks/useDropdownFormInitializer';

// Type with required id for the CrudPage
type DemandWithId = DemandDto & { id: string };

export default function DemandCrudFormDialog({ value, onClose, onSave }: CrudFormProps<DemandWithId>) {
  const { t } = useTranslation(['common']);
  const [materials, setMaterials] = useState<MaterialDto[]>([]);
  const [deliveryTimes, setDeliveryTimes] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(false);

  const demandTypeOptions = [
    { text: t('forms.demand.sourceOrder'), value: 'ORDER' },
    { text: t('forms.demand.sourceForecast'), value: 'FORECAST' }
  ];

  // Load data function for the hook
  const loadData = async () => {
    const [materialsData, timesData] = await Promise.all([
      MaterialControllerService.findAll(),
      SystemSettingControllerService.getDeliveryTimes().catch(() => [])
    ]);
    const makeMaterials = materialsData.filter(material => material.productionType === 'MAKE');
    const times = Array.isArray(timesData) ? timesData : [];
    return { materials: makeMaterials, deliveryTimes: times };
  };

  // Set state functions for the hook
  const setStateFunctions = {
    materials: setMaterials,
    deliveryTimes: setDeliveryTimes
  };

  // Use the custom hook for proper timing
  useDropdownFormInitializer(
    visible,
    loadData,
    setStateFunctions,
    () => setLoading(false),
    {}
  );

  useEffect(() => {
    setVisible(true);
  }, []);

  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';
  const fieldClass = 'mb-4';

  return (
    <FormDialog<DemandWithId>
      value={value}
      onClose={onClose}
      onSave={onSave}
      titleKey={value?.uuid ? 'forms.demand.edit' : 'forms.demand.add'}
      width="720px"
    >
      {({ register, formState, setValue, watch }) => (
        <>
          <input type="hidden" {...register('materialId', { required: t('validation.required') })} />

          {/* Row 1: material */}
          <div className={fieldClass}>
            <label className={labelClass}>{t('forms.demand.materialId')} *</label>
            {loading ? (
              <div className={`${inputClass} bg-gray-100`}>{t('common.loading')}</div>
            ) : (
              <DropDownListComponent
                dataSource={materials}
                fields={{ text: 'name', value: 'uuid' }}
                placeholder={t('forms.demand.selectMaterial')}
                cssClass={formState.errors.materialId ? 'e-error' : ''}
                value={watch('materialId')}
                onChange={(e: any) => {
                  setValue('materialId', e.value, { shouldValidate: true, shouldDirty: true });
                }}
              />
            )}
            {formState.errors.materialId && (
              <span className="text-red-500 text-sm">{resolveValidationMessage(t, formState.errors.materialId)}</span>
            )}
          </div>

          {/* Row 2: demand date | due time */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className={fieldClass}>
              <label className={labelClass}>{t('forms.demand.demandDate')} *</label>
              <input
                type="date"
                {...register('demandDate', { required: t('validation.required') })}
                className={inputClass}
              />
              {formState.errors.demandDate && (
                <span className="text-red-500 text-sm">{resolveValidationMessage(t, formState.errors.demandDate)}</span>
              )}
            </div>
            <div className={fieldClass}>
              <label className={labelClass}>{t('forms.demand.dueTime')}</label>
              <DropDownListComponent
                dataSource={[
                  { value: '', text: t('forms.demand.dueTimeDefault', 'Default (erste Auslieferzeit)') },
                  ...deliveryTimes.map((time) => ({ value: time.length >= 5 ? time.slice(0, 5) : time, text: time.length >= 5 ? time.slice(0, 5) : time }))
                ]}
                fields={{ text: 'text', value: 'value' }}
                placeholder={t('forms.demand.dueTime')}
                value={watch('dueTime') != null ? (String(watch('dueTime')).slice(0, 5) || '') : ''}
                onChange={(e: any) => setValue('dueTime', e.value ? String(e.value) : undefined, { shouldValidate: true, shouldDirty: true })}
              />
            </div>
          </div>

          {/* Row 3: quantity | demand type */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className={fieldClass}>
              <label className={labelClass}>{t('forms.demand.quantity')} *</label>
              <input
                type="number"
                min="0"
                step="0.0001"
                {...register('quantity', {
                  required: t('validation.required'),
                  min: 0,
                  valueAsNumber: true,
                  setValueAs: (value) => {
                    const num = parseFloat(value);
                    return isNaN(num) ? 0 : num;
                  }
                })}
                className={inputClass}
              />
              {formState.errors.quantity && (
                <span className="text-red-500 text-sm">
                  {formState.errors.quantity.type === 'required' ? t('validation.required') : t('validation.min', { min: 0 })}
                </span>
              )}
            </div>
            <div className={fieldClass}>
              <label className={labelClass}>{t('forms.demand.demandType', 'Bedarfsart')}</label>
              <DropDownListComponent
                dataSource={demandTypeOptions}
                fields={{ text: 'text', value: 'value' }}
                placeholder={t('forms.demand.demandType')}
                value={watch('demandType') ?? 'ORDER'}
                onChange={(e: any) => setValue('demandType', e.value)}
              />
            </div>
          </div>

          {/* Customer order number (the demand id is computed from the settings) */}
          <div className="mb-4">
            <div className={fieldClass}>
              <label className={labelClass}>{t('forms.demand.customerOrderNumber')}</label>
              <input
                type="text"
                {...register('customerOrderNumber')}
                className={inputClass}
                placeholder={t('forms.demand.customerOrderNumberPlaceholder')}
              />
            </div>
          </div>
        </>
      )}
    </FormDialog>
  );
} 